import React from "react";
import Card from "components/card";
import { Rating } from "@aws-amplify/ui-react";
import { usePortfolioData } from "contexts/PortfolioContext";
import { IoBusinessOutline } from "react-icons/io5";
import { IoOpenOutline } from "react-icons/io5";
import { TbBuildingCommunity } from "react-icons/tb";
import SolidSubtleAlert from "components/alert/SolidSubtleAlert";
import MissingCCNAlert from "components/alert/MissingCCNAlert";
import { useTabs } from "contexts/TabContext";

const PortfolioTitle = () => {
    const {
        currentPortfolioName,
        currentPortfolioFacilityName,
        currentPortfolioFacility,
        currentPortfolio,
        selectedPeriod,
        portfolioFacilityNames,
        uniqueFacilityCount,
        currentGroup,
        portfolioFacilityGroups
    } = usePortfolioData();

    const { addTab } = useTabs();

    // Function to handle competition icon click and create tab
    const handleCompetitionClick = () => {
        // Create the path including the portfolio name if available
        let path;
        if (currentPortfolioName) {
            const baseUrl = `/portfolio/${encodeURIComponent(currentPortfolioName)}`;
            if (currentPortfolioFacilityName && currentPortfolioFacilityName !== "Consolidated") {
                path = `${baseUrl}/${encodeURIComponent(currentPortfolioFacilityName)}/competition-charts/competition-analysis`;
            } else {
                path = `${baseUrl}/competition-charts/competition-analysis`;
            }
        } else {
            path = `/competition-charts/competition-analysis`;
        }

        // Add the tab using the context
        addTab('competition-analysis', 'Competition Analysis', path);
    };


    // Check if current selection is a group (exclude consolidated facilities)
    const isGroup = (currentPortfolioFacility?.details?.isGroup || currentPortfolioFacility?._isGroupData || !!currentGroup) &&
        !(currentPortfolioFacilityName === "Consolidated" && !currentGroup);

    // Determine the title based on current selections
    const facilityTitle = isGroup
        ? `${currentPortfolioName} - ${currentPortfolioFacility?.details?.Facilities}`
        : currentPortfolioFacilityName === "Consolidated" || !currentPortfolioFacilityName
            ? (currentPortfolioName || "Portfolio Overview")
            : `${currentPortfolioName} - ${currentPortfolioFacilityName}`;

    const totalFacilities = uniqueFacilityCount || 0;

    // Get facility details if available
    const facilityDetails = currentPortfolioFacility?.details;

    // Determine if we're viewing a specific facility vs consolidated view
    const isSpecificFacilitySelected = currentPortfolioFacilityName !== "Consolidated" && !isGroup;

    // Build full address for individual facilities
    const buildFullAddress = () => {
        if (!facilityDetails) return "";

        const addressParts = [];

        // Add street address
        const streetAddress = facilityDetails?.Address ||
            facilityDetails?.["Street Address"] ||
            facilityDetails?.Street || "";
        if (streetAddress) {
            addressParts.push(streetAddress);
        }

        // Add city
        const city = facilityDetails?.City || facilityDetails?.Location || "";
        if (city) {
            addressParts.push(city);
        }
        return addressParts.join(", ");
    };

    const facilityLocation = facilityDetails?.Location || facilityDetails?.City || "";
    const facilityState = facilityDetails?.State || "";
    const fullLocation = isSpecificFacilitySelected ? buildFullAddress() :
        (facilityLocation && facilityState
            ? `${facilityLocation}, ${facilityState}`
            : facilityLocation || facilityState);
    const isConsolidatedView = !isSpecificFacilitySelected && !isGroup;

    // Get consolidated portfolio data (similar to DealTitle's consolidatedFacility)
    const consolidatedPortfolioFacility = currentPortfolio?.facilities?.Consolidated || currentPortfolio?.facilities?.consolidated;

    // For AL/IL facilities, show only AL/IL/MC beds instead of the full building total
    const isALFacility = facilityDetails?.isALDuplicate;
    const totalBeds = isALFacility
        ? (parseFloat(facilityDetails?.["ALF Beds"]) || 0) +
          (parseFloat(facilityDetails?.["ILF Beds"]) || 0) +
          (parseFloat(facilityDetails?.["MC Beds"]) || 0) ||
          facilityDetails?.Beds || facilityDetails?.["Total Beds"] || 0
        : facilityDetails?.Beds || facilityDetails?.["Total Beds"] || 0;
    const facilityType = facilityDetails?.Type || "";

    // Get consolidated rating (always show portfolio overall rating)
    const consolidatedRating = Number(
        consolidatedPortfolioFacility?.details?.["Star Rating"] ||
        consolidatedPortfolioFacility?.details?.["Overall Rating"] ||
        consolidatedPortfolioFacility?.details?.Rating ||
        consolidatedPortfolioFacility?.details?.["CMS Star Rating"] ||
        0
    );

    // Get consolidated beds (always show portfolio total beds)
    const consolidatedBeds = consolidatedPortfolioFacility?.details?.["Total Beds"] || 0;

    // Facilities with no SNF beds are AL facilities and don't have a CCN — skip them
    const hasSnfBeds = (facility) =>
        (parseInt(facility?.details?.["SNF Beds"]) || 0) > 0;

    // Determine facilities missing a CCN (for consolidated view alert)
    const facilitiesMissingCCN = React.useMemo(() => {
        if (!currentPortfolio?.facilities) return [];
        return Object.entries(currentPortfolio.facilities)
            .filter(([name, facility]) =>
                name.toLowerCase() !== 'consolidated' &&
                !facility?.details?.isGroup &&
                !facility?._isGroupData &&
                hasSnfBeds(facility) &&
                !facility?.details?.["CCN"]
            )
            .map(([name, facility]) =>
                facility?.details?.Facilities ||
                facility?.details?.["Facility Name"] ||
                name
            );
    }, [currentPortfolio]);

    const currentFacilityMissingCCN =
        isSpecificFacilitySelected &&
        hasSnfBeds(currentPortfolioFacility) &&
        !facilityDetails?.["CCN"];

    // Get all unique states from portfolio facilities for consolidated view
    const portfolioStates = React.useMemo(() => {
        if (!currentPortfolio?.facilities || isSpecificFacilitySelected) return [];

        const states = new Set();
        Object.values(currentPortfolio.facilities).forEach(facility => {
            if (facility.details?.State && facility.details.State !== "Consolidated") {
                states.add(facility.details.State);
            }
        });

        return Array.from(states).sort();
    }, [currentPortfolio, isSpecificFacilitySelected]);



    return (
        <div className="flex flex-col gap-2 w-full">
            {(currentFacilityMissingCCN || (!isSpecificFacilitySelected && !isGroup && facilitiesMissingCCN.length > 0)) && (
                <MissingCCNAlert
                    isSpecificFacility={!!currentFacilityMissingCCN}
                    missingFacilities={facilitiesMissingCCN}
                    resetKey={`${currentPortfolioName}|${currentPortfolioFacilityName || ''}`}
                />
            )}
            <Card extra={"sm:!flex-row gap-2 w-full h-full p-[16px] bg-cover sm:divide-x"}>
            <div className="sm:basis-[40%] sm:m-2 flex flex-col items-center w-full justify-center rounded-xl border-l-1">
                <h2 className="mb-2 text-4xl text-center font-bold text-navy-700 dark:text-white">
                    {currentPortfolioName}
                </h2>

                {/* Consolidated View - Show States */}
                {!isSpecificFacilitySelected && !isGroup && (
                    <div className="flex items-center">
                        <p className="text-xl text-gray-800 dark:text-gray-400">
                            {portfolioStates.length === 1
                                ? portfolioStates[0]
                                : portfolioStates.join(', ')
                            }
                        </p>
                        <button
                            onClick={handleCompetitionClick}
                            className="ml-3 p-1 hover:bg-gray-100 rounded-full transition-colors"
                            title="View competition data"
                        >
                            <TbBuildingCommunity className="h-5 w-5 text-[#755263]" />
                        </button>
                    </div>
                )}

                {/* Group Title */}
                {isGroup && (
                    <div className="flex items-center">
                        <p className="text-xl text-gray-800 dark:text-gray-400">
                            {currentPortfolioFacility?.details?.Facilities}
                        </p>
                        <button
                            onClick={handleCompetitionClick}
                            className="ml-3 p-1 hover:bg-gray-100 rounded-full transition-colors"
                            title="View competition data"
                        >
                            <TbBuildingCommunity className="h-5 w-5 text-[#755263]" />
                        </button>
                    </div>
                )}

                {/* Individual Facility Title */}
                {isSpecificFacilitySelected && (
                    <div className="flex items-center">
                        <div className="flex items-center">
                            <p className="text-xl text-gray-800 dark:text-gray-400">
                                {currentPortfolioFacility?.details?.Facilities}
                            </p>
                            {fullLocation && (
                                <>
                                    <span className="mx-2 text-gray-600 dark:text-gray-400">|</span>
                                    <p className="text-md capitalize text-gray-600 dark:text-gray-400">
                                        {fullLocation}
                                    </p>
                                </>
                            )}
                        </div>
                        <button
                            onClick={handleCompetitionClick}
                            className="ml-3 p-1 hover:bg-gray-100 rounded-full transition-colors"
                            title="View competition data"
                        >
                            <TbBuildingCommunity className="h-5 w-5 text-[#755263]" />
                        </button>
                    </div>
                )}
                <div className="flex divide-x w-full justify-center">
                    {isSpecificFacilitySelected && facilityDetails?.["CCN"] && (
                        <a
                            href={`https://www.medicare.gov/care-compare/details/nursing-home/${facilityDetails?.["CCN"]}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-blue-600 text-left font-normal flex items-center gap-1 hover:underline transition-colors"
                        >
                            View at Medicare.gov
                            <IoOpenOutline className="ml-1" />
                        </a>
                    )}
                </div>
            </div>

            <div className="flex flex-row basis-[60%] justify-around divide-x w-full h-full gap-0">
                {/* Facilities Count Column */}
                <div className="px-2 flex flex-col basis-1/3 items-center justify-center">
                    <h4 className="text-2xl font-bold text-navy-700 dark:text-white">
                        {totalFacilities}
                    </h4>
                    <p className="text-center font-normal text-gray-600">
                        Facilities in Portfolio
                    </p>

                    {/* Group Alert - similar to DealTitle */}
                    {isGroup && (
                        <div className="mt-3 mx-3 p-4 bg-blue-50 rounded-lg text-sm w-full">
                            <div className="flex flex-col items-center">
                                <p className="text-sm font-medium text-blue-800">
                                    Group: {currentGroup?.facilityIds?.length || 0} facilities
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Facility Type Alert for Individual Facilities */}
                    {isSpecificFacilitySelected && facilityType && (
                        <div className="mt-3 px-4 w-full items-center justify-center">
                            <SolidSubtleAlert
                                title={facilityType}
                                description="Facility Type"
                                icon={<IoBusinessOutline />}
                                iconColor="text-blue-600"
                                bg="bg-blue-50 dark:!bg-navy-700"
                                closeBg="hover:bg-white/20 text-blue-900 dark:text-white"
                            />
                        </div>
                    )}
                </div>

                {/* Star Rating Column */}
                <div className="p-2 flex flex-col gap-2 basis-1/3 items-center justify-center">
                    <div className="px-2 flex flex-col basis-2/3 items-center justify-center">
                        <Rating value={consolidatedRating} fillColor="#f0b41c" />
                        <p className="mt-1 font-normal text-center text-gray-600">
                            Portfolio Overall Rating
                        </p>
                    </div>
                </div>

                {/* Beds Column */}
                <div className="p-2 flex flex-col gap-2 basis-1/3 items-center justify-center">
                    <div className="flex flex-col items-center justify-center">
                        <h4 className="text-xl font-bold text-navy-700 dark:text-white">
                            {consolidatedBeds || "N/A"}
                        </h4>
                        <p className="text-center font-normal text-gray-600">Total Beds</p>
                    </div>

                    {/* Show group or specific facility beds */}
                    {(isSpecificFacilitySelected || isGroup) && (
                        <div className="flex flex-col items-center justify-center">
                            <h4 className="text-lg font-bold text-navy-700 dark:text-white">
                                {totalBeds || "N/A"}
                            </h4>
                            <p className="text-center text-sm text-gray-600">
                                {isGroup ? "Group Beds" : "Facility Beds"}
                            </p>
                        </div>
                    )}
                </div>
            </div>

        </Card>
        </div>
    );
};

export default PortfolioTitle;